import {LoadingDots} from '#/atoms/dots-loading-indicator';
import {DateValueLineBarGraph} from '#/atoms/graphs/date-value-line-bar-graph';
import {WarningMessageAction} from '#/atoms/warning-message-action';
import {TileFilterButton} from '#/components/buttons/filter-button';
import GraphRangeSelector from '#/components/graphs/graph-range-selector';
import {getEquidistantRangeFromMinMax} from '#/components/graphs/graphs-helpers';
import {getLandGraphDataMinMaxValues, useLandGraphData} from '#/components/graphs/use-land-graph-data';
import {useObjectQueueSet} from '#/hooks/use-queue-set';
import {ClassName} from '#/utils/utils';
import {CurrencyDollarIcon} from '@heroicons/react/24/outline';
import {ArrowTrendingUpIcon, ChartBarIcon} from '@heroicons/react/24/solid';
import {FunctionComponent, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {GraphRange, Land} from 'shareland-common';

type LandMarketFilterKeys = 'oraclePrice' | 'marketPrice' | 'volume';
const initialFilterKeys = ['marketPrice', 'volume'] as LandMarketFilterKeys[];

const LandMarketDataGraph: FunctionComponent<{land: Land; graphRange: GraphRange} & ClassName> = ({
  land,
  graphRange,
  className = '',
}) => {
  const {t} = useTranslation('translation');

  const objectQueueSetFilters = useObjectQueueSet<LandMarketFilterKeys>({initial: initialFilterKeys});

  const {data = [], isLoading, refetch} = useLandGraphData(graphRange, land.tokenSymbol);
  const minAndMaxValues = useMemo(
    () => getLandGraphDataMinMaxValues(data!, objectQueueSetFilters.values),
    [data, objectQueueSetFilters],
  );
  const verticalLineTicks = useMemo(() => getEquidistantRangeFromMinMax(minAndMaxValues), [minAndMaxValues]);

  const showNoData = !isLoading && data.length === 0;
  const showLoading = isLoading && objectQueueSetFilters.values.length > 0;
  const showNoFiltersSelected = !isLoading && objectQueueSetFilters.values.length === 0;
  const showGraph = !(showNoData || showLoading || showNoFiltersSelected);
  const showTicketFilters = !(showNoData || showLoading);

  return (
    <div className={`space-y-4 ${className}`}>
      <LandMarketTickerFilters
        className={showTicketFilters ? '' : 'grayscale pointer-events-none'}
        objectQueueSet={objectQueueSetFilters}
      />

      {showNoData && (
        <WarningMessageAction
          className='h-[220px] pt-[10px] pb-[5px] text-sm text-white font-semibold'
          iconStrokeClassName='stroke-deepblue'
          message={t('components.warningMessageAction.noDataToShowForLocation.message')}
          buttonContent={t('components.warningMessageAction.noDataToShowForLocation.cta')}
          action={refetch}
        />
      )}

      {showLoading && (
        <div className='w-full h-[220px] pt-[10px] pb-[5px] flex flex-col justify-center items-center'>
          <LoadingDots widthClassName='w-3' gapClassName='gap-2' />
        </div>
      )}

      {showNoFiltersSelected && (
        <WarningMessageAction
          className='h-[220px] pt-[10px] pb-[5px] text-sm text-white font-semibold'
          iconStrokeClassName='stroke-deepblue'
          message={t('components.warningMessageAction.noFiltersSelected.message')}
          buttonContent={t('components.warningMessageAction.noFiltersSelected.cta')}
          action={objectQueueSetFilters.reset}
        />
      )}

      <DateValueLineBarGraph
        className={showGraph ? '' : 'hidden'}
        data={data}
        activeDataKeys={objectQueueSetFilters.values}
        yLineTicks={verticalLineTicks}
        graphHeight={220}
        graphMargins={{top: 10, right: 10, bottom: 5, left: 10}}
        barHeight={60}
        barPadding={10}
      />

      <GraphRangeSelector className={showGraph ? '' : 'grayscale pointer-events-none'} />
    </div>
  );
};

const LandMarketTickerFilters: FunctionComponent<
  {
    objectQueueSet: ReturnType<typeof useObjectQueueSet<LandMarketFilterKeys>>;
  } & ClassName
> = ({objectQueueSet, className}) => {
  const {t} = useTranslation('translation');

  const commonProps = {
    selectedFilters: objectQueueSet.values,
    setFilter: objectQueueSet.open,
    removeFilter: objectQueueSet.close,
  };

  return (
    <div className={`w-full items-center justify-end flex gap-2 ${className}`}>
      <TileFilterButton<LandMarketFilterKeys>
        {...commonProps}
        filter={'oraclePrice'}
        title={t('components.landMarketDataGraph.fields.oraclePrice.title')}
        IconComponent={CurrencyDollarIcon}
        iconColorClassName={'fill-transparent stroke-grayblue'}
        primaryTextColor={'text-grayblue'}
        bgColor={'bg-[#90EE79]'}
      />

      <TileFilterButton<LandMarketFilterKeys>
        {...commonProps}
        filter={'marketPrice'}
        title={t('components.landMarketDataGraph.fields.marketPrice.title')}
        IconComponent={ArrowTrendingUpIcon}
        iconColorClassName='fill-grayblue stroke-grayblue'
        primaryTextColor='text-grayblue'
        bgColor='bg-[#3182ce]'
      />

      <TileFilterButton<LandMarketFilterKeys>
        {...commonProps}
        filter={'volume'}
        title={t('components.landMarketDataGraph.fields.volume.title')}
        IconComponent={ChartBarIcon}
        iconColorClassName='fill-grayblue stroke-grayblue'
        primaryTextColor='text-grayblue'
        bgColor='bg-[#FFA500]'
      />
    </div>
  );
};

export default LandMarketDataGraph;
